import { processSearchQueryForSearchtext } from './scimSearchUtils';

export type SimpleSearchDialectMode = 'auto' | 'expression' | 'freetext';
export type ResolvedSimpleSearchDialect = Exclude<SimpleSearchDialectMode, 'auto'>;
export type AutoDetectedSearchKind = 'freetext' | 'expression' | 'manual';

export interface ResolvedSimpleSearchPlan {
    rawQuery: string;
    searchText?: string;
    filter?: string;
    dialect: ResolvedSimpleSearchDialect;
}

function escapeScimFilterValue(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function normalizeDisplayNamePrefix(query: string): { broadSearchText: string; displayNamePrefix: string } | null {
    const match = query.match(/^\s*([^,]+?)\s*,\s*([^,\s].*?)\s*$/);
    if (!match) return null;

    const broadSearchText = (match[1] || '').trim();
    const trailing = (match[2] || '').trim();
    if (!broadSearchText || !trailing) return null;

    return {
        broadSearchText,
        displayNamePrefix: `${broadSearchText}, ${trailing}`
    };
}

function looksLikeExpressionSearch(query: string): boolean {
    const trimmed = query.trim();
    if (!trimmed) return false;

    if (/\b(?:NEAR|ISABOUT|FORMSOF)\b/i.test(trimmed)) return true;
    if (/[()*~]/.test(trimmed)) return true;
    if (/".+?"/.test(trimmed)) return true;

    // Treat plain boolean operators as expression intent when the user writes them explicitly
    // (typically uppercase) or when they connect already-quoted terms. This avoids pushing
    // natural-language searches like "research and development" into expression mode.
    if (/\b(?:AND(?:\s+NOT)?|OR|NOT)\b/.test(trimmed)) return true;
    if (/".+?"\s+(?:and(?:\s+not)?|or|not)\s+".+?"/i.test(trimmed)) return true;

    return false;
}

function looksLikeManualScimFilter(query: string): boolean {
    const trimmed = query.trim();
    if (!trimmed) return false;

    const attributePattern = String.raw`[A-Za-z][\w:-]*(?:\.[A-Za-z][\w:-]*)*`;
    const comparisonValuePattern = String.raw`(?:"(?:\\.|[^"])*"|'(?:\\.|[^'])*'|true|false|null|-?\d+(?:\.\d+)?)`;
    const comparisonPattern = new RegExp(
        String.raw`^\s*\(*\s*(?:not\s+)?${attributePattern}\s+(?:eq|ne|co|sw|ew|gt|ge|lt|le)\s+${comparisonValuePattern}(?:\s*(?:\)|$)|\s+\b(?:and|or)\b)`,
        'i'
    );
    const presencePattern = new RegExp(
        String.raw`^\s*\(*\s*(?:not\s+)?${attributePattern}\s+pr(?:\s*(?:\)|$)|\s+\b(?:and|or)\b)`,
        'i'
    );
    const countPattern = new RegExp(
        String.raw`^\s*\(*\s*(?:not\s+)?${attributePattern}\s+\$count\.(?:gt|ge|lt|le|eq|ne)\s+-?\d+(?:\.\d+)?(?:\s*(?:\)|$)|\s+\b(?:and|or)\b)`,
        'i'
    );

    return comparisonPattern.test(trimmed) || presencePattern.test(trimmed) || countPattern.test(trimmed);
}

export function detectAutoSearchKind(query: string): AutoDetectedSearchKind {
    const trimmed = query.trim();
    if (!trimmed) return 'freetext';
    if (looksLikeManualScimFilter(trimmed)) return 'manual';
    if (looksLikeExpressionSearch(trimmed)) return 'expression';
    return 'freetext';
}

export function combineScimFiltersWithAnd(...filters: Array<string | null | undefined>): string | undefined {
    const normalized = filters
        .map((filter) => (filter ?? '').trim())
        .filter(Boolean);

    if (normalized.length === 0) return undefined;
    if (normalized.length === 1) return normalized[0];
    return normalized.map((filter) => `(${filter})`).join(' and ');
}

export function resolveSimpleSearchPlan(
    query: string,
    searchAttributes?: string[],
    dialectMode: SimpleSearchDialectMode = 'auto'
): ResolvedSimpleSearchPlan {
    const rawQuery = (query || '').trim();
    const autoKind = detectAutoSearchKind(rawQuery);
    const dialect: ResolvedSimpleSearchDialect = dialectMode === 'auto'
        ? (autoKind === 'expression' ? 'expression' : 'freetext')
        : dialectMode;

    if (!rawQuery) {
        return { rawQuery, dialect };
    }

    if (dialectMode === 'auto') {
        const displayNamePrefix = normalizeDisplayNamePrefix(rawQuery);
        if (displayNamePrefix) {
            return {
                rawQuery,
                searchText: processSearchQueryForSearchtext(displayNamePrefix.broadSearchText, searchAttributes) ?? displayNamePrefix.broadSearchText,
                filter: `DisplayName sw "${escapeScimFilterValue(displayNamePrefix.displayNamePrefix)}"`,
                dialect
            };
        }
    }

    // When in freetext mode and the query is plain text (no expression operators,
    // no manual SCIM filter), use a SCIM "co" filter for substring matching instead
    // of backend full-text searchText. SQL Server's FREETEXT tokenizes compound words
    // (e.g. "Scorpius3") as single tokens and won't match partial prefixes ("scorpius").
    // SCIM "co" uses SQL LIKE which provides intuitive substring matching.
    // Only apply when using default search attributes (DisplayName); when the user has
    // selected custom search attributes, fall back to full-text searchText so the
    // backend can search across those attributes.
    const hasCustomSearchAttributes = searchAttributes
        && searchAttributes.some(a => {
            const lower = (a ?? '').toLowerCase();
            return lower !== 'displayname' && lower !== 'id' && lower !== '';
        });

    if (dialect === 'freetext' && autoKind !== 'manual' && !hasCustomSearchAttributes) {
        const escaped = escapeScimFilterValue(rawQuery);
        return {
            rawQuery,
            searchText: undefined,
            filter: `DisplayName co "${escaped}" or Id co "${escaped}"`,
            dialect
        };
    }

    return {
        rawQuery,
        searchText: processSearchQueryForSearchtext(rawQuery, searchAttributes) ?? rawQuery,
        filter: undefined,
        dialect
    };
}
